import { Schedule, APPOINTMENT_TYPES } from "@/types/schedule";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ConflictResolutionModalProps {
  existingSchedule: Schedule | null;
  newSchedule: Omit<Schedule, "id"> | null;
  isOpen: boolean;
  onResolve: (action: "replace" | "keep-both" | "cancel") => void;
}

export const ConflictResolutionModal = ({
  existingSchedule,
  newSchedule,
  isOpen,
  onResolve,
}: ConflictResolutionModalProps) => {
  if (!existingSchedule || !newSchedule) return null;

  const getOverlapTime = () => {
    const start = existingSchedule.startTime > newSchedule.startTime 
      ? existingSchedule.startTime 
      : newSchedule.startTime;
    const end = existingSchedule.endTime < newSchedule.endTime 
      ? existingSchedule.endTime 
      : newSchedule.endTime;
    return { start, end };
  };

  const overlap = getOverlapTime();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onResolve("cancel")}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <DialogTitle>Conflito de Horário Detectado</DialogTitle>
          </div>
          <DialogDescription>
            Já existe um horário cadastrado que se sobrepõe ao novo horário.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <h4 className="font-semibold text-sm">Horário Existente:</h4>
            <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-md border border-red-200 dark:border-red-800">
              <p className="text-sm">
                <span className="font-medium">Horário:</span> {existingSchedule.startTime} - {existingSchedule.endTime}
              </p>
              <p className="text-sm">
                <span className="font-medium">Tipo:</span> {APPOINTMENT_TYPES[existingSchedule.appointmentType].label}
              </p>
              <p className="text-sm">
                <span className="font-medium">Validade:</span>{" "}
                {format(existingSchedule.startDate, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                {existingSchedule.endDate
                  ? format(existingSchedule.endDate, "dd/MM/yyyy", { locale: ptBR })
                  : "Indeterminado"}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-sm">Novo Horário:</h4>
            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-md border border-blue-200 dark:border-blue-800">
              <p className="text-sm">
                <span className="font-medium">Horário:</span> {newSchedule.startTime} - {newSchedule.endTime}
              </p>
              <p className="text-sm">
                <span className="font-medium">Tipo:</span> {APPOINTMENT_TYPES[newSchedule.appointmentType].label}
              </p>
              <p className="text-sm">
                <span className="font-medium">Validade:</span>{" "}
                {format(newSchedule.startDate, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                {newSchedule.endDate
                  ? format(newSchedule.endDate, "dd/MM/yyyy", { locale: ptBR })
                  : "Indeterminado"}
              </p>
            </div>
          </div>

          <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-md border border-orange-200 dark:border-orange-800">
            <p className="text-sm font-medium text-orange-700 dark:text-orange-400">
              ⚠️ Sobreposição: {overlap.start} - {overlap.end}
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="destructive"
            onClick={() => onResolve("replace")}
            className="w-full sm:w-auto"
          >
            Substituir o Antigo
          </Button>
          <Button
            variant="outline"
            onClick={() => onResolve("keep-both")}
            className="w-full sm:w-auto"
          >
            Manter Ambos
          </Button>
          <Button
            variant="secondary"
            onClick={() => onResolve("cancel")}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

